import { Settings } from '@velocity-exchange/icons';
import { useEffect, useRef, useState } from 'react';
import PopoverWrapper from 'src/components/PopoverWrapper';
import SettingsSwitch from 'src/components/Settings/components/SettingsSwitch';
import TopBarIconButton from 'src/components/TopBar/TopBarIconButton';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useTargetedPopover from 'src/hooks/useTargetedPopover';
import {
	isDev,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import Button from 'src/components/primitives/Button/Button';
import { TRADE_PAGE_CONTAINER_PADDING_X } from 'src/components/TradePageGrid/consts';
import useInterfaceSettings from 'src/components/Settings/InterfaceSettings/hooks/useInterfaceSettings';
import Divider from 'src/components/primitives/Divider/Divider';
import ThemeSettingButtonGroup from 'src/components/Settings/InterfaceSettings/ThemeSettingButtonGroup';
import { handleRefresh } from 'src/utils/dom';
import useModal from 'src/components/Modals/hooks/useModal';
import { cj } from 'src/utils/classNames';
import { TOP_BAR_MOBILE_PADDING } from 'src/components/TopBar/consts';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import Link from 'src/components/primitives/Link/Link';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';
import { useSubAccountsDelegation } from 'src/hooks/wallets/privy/useSubAccountsDelegation';
import PresetTag from 'src/components/Tags/PresetTag';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';

export default function InterfaceSettingsPopup() {
	const { show } = useModal();
	const ref = useRef<HTMLButtonElement>(null);
	const isMobileScreenSize = useIsMobileScreenSize();
	const [settings, updateSettings] = useCurrentSettings();
	const isDevEnv = isDev();
	const appEventEmitter = useAppEventEmitter();
	const { embeddedWallet, isPrivyNativeAuth } = usePrivyWalletContext();
	const { isEmbeddedWalletTheDelegateOfCurrentUserAccount } = useWalletsV2();
	const { isCurrentUserAccountDelegatable } = useSubAccountsDelegation();

	const [hasSeenAutoSignDelegatedTooltip, setHasSeenAutoSignDelegatedTooltip] =
		useSyncLocalStorage<boolean>('hasSeenAutoSignDelegatedTooltip', false);

	const [showAutoSigningTooltip, setShowAutoSigningTooltip] = useState(false);

	const isDelegatedButNotToEmbeddedWallet =
		!!embeddedWallet &&
		!isCurrentUserAccountDelegatable &&
		!isEmbeddedWalletTheDelegateOfCurrentUserAccount;

	const showAutoSigningSettingSwitch =
		!isPrivyNativeAuth && isDelegatedButNotToEmbeddedWallet;

	const handleAutoSigningTooltipOpenChange = (nextOpen: boolean): void => {
		if (!nextOpen) {
			setShowAutoSigningTooltip(false);
		}
	};

	const {
		refs,
		floatingStyles,
		getFloatingProps,
		setIsPopoverOpen,
		isPopoverOpen,
	} = useTargetedPopover(
		{
			strategy: 'fixed',
			placement: 'bottom-start',
			elements: {
				reference: ref.current,
			},
		},
		{ disableAutoPlacement: true }
	);

	const toggleSettingsPopup = () => {
		setIsPopoverOpen(!isPopoverOpen);
	};

	const showSettingsModal = () => {
		setIsPopoverOpen(false);
		show({ type: 'settings', props: {} });
	};

	const updateSetting = (key: keyof UserSettings) => {
		updateSettings({ ...settings, [key]: !settings[key] });

		if (key === 'devnetOverride') {
			handleRefresh('About to refresh to change Environment');
			return;
		}
	};

	const { generalSettings, chartSettings } = useInterfaceSettings();

	const left = isMobileScreenSize
		? `-${TOP_BAR_MOBILE_PADDING.horizontal}px`
		: `-${TRADE_PAGE_CONTAINER_PADDING_X}px`;

	// set up event listener for showing the auto signing tooltip
	useEffect(() => {
		if (hasSeenAutoSignDelegatedTooltip) return;

		const handleShowAutoSigningTooltip = () => {
			if (!isDelegatedButNotToEmbeddedWallet) return;

			setShowAutoSigningTooltip(true);
			setHasSeenAutoSignDelegatedTooltip(true);
		};

		appEventEmitter.on(
			'showSettingsAutoSigningTooltip',
			handleShowAutoSigningTooltip
		);

		return () => {
			appEventEmitter.off(
				'showSettingsAutoSigningTooltip',
				handleShowAutoSigningTooltip
			);
		};
	}, [
		appEventEmitter,
		hasSeenAutoSignDelegatedTooltip,
		isDelegatedButNotToEmbeddedWallet,
		setShowAutoSigningTooltip,
		setHasSeenAutoSignDelegatedTooltip,
	]);

	// set up timer for hiding the auto signing tooltip
	useEffect(() => {
		if (showAutoSigningTooltip) {
			const timer = setTimeout(() => {
				setShowAutoSigningTooltip(false);
			}, 10000);

			return () => clearTimeout(timer);
		}
	}, [showAutoSigningTooltip]);

	return (
		<>
			<Tooltip
				open={showAutoSigningTooltip}
				onOpenChange={handleAutoSigningTooltipOpenChange}
				delayDuration={0}
				content={
					<div className="flex flex-col gap-y-xs">
						Now you can turn on the switch in Settings to enable Auto-Signing
						for your delegated account. This will save you time and make your
						trading experience much faster.
						<br />
						<br />
						<Link
							href="https://docs.drift.trade/getting-started/delegated-accounts"
							openInNewTab
						>
							Learn more
						</Link>
					</div>
				}
				decoration="none"
				asChild
				disableHoverableContent
			>
				<TopBarIconButton
					onClick={toggleSettingsPopup}
					icon={Settings}
					ref={ref}
				/>
			</Tooltip>
			{isPopoverOpen && (
				<PopoverWrapper
					ref={refs.setFloating}
					style={{
						...floatingStyles,
						left,
					}}
					{...getFloatingProps()}
					className={cj(
						'flex max-h-[90vh] w-[300px] flex-col shadow-lg',
						'rounded-sm border border-input-border bg-menu-bg p-md'
					)}
				>
					<div className="hidden-scroll flex grow flex-col gap-y-lg overflow-y-auto pb-md">
						{showAutoSigningSettingSwitch && (
							<>
								<SettingsSwitch
									label={
										<span className="flex items-center gap-x-xs">
											Enable Auto-Signing
											<PresetTag preset="new" />
										</span>
									}
									checked={false}
									onChange={(_checked) => {
										toggleSettingsPopup();
										show({
											type: 'enable_signless_trading_for_delegated',
											props: {},
										});
									}}
								/>
								<Divider />
							</>
						)}

						{generalSettings.map(({ label, key }) => (
							<SettingsSwitch
								key={key}
								label={label}
								checked={!!settings[key]}
								onChange={() => updateSetting(key)}
							/>
						))}
						<Divider />
						{chartSettings.map(({ label, key }) => (
							<SettingsSwitch
								key={key}
								label={label}
								checked={!!settings[key]}
								onChange={() => updateSetting(key)}
							/>
						))}
						<Divider />
						<ThemeSettingButtonGroup
							value={settings.theme}
							onChange={(theme) => updateSettings({ theme })}
						/>
						{isDevEnv && (
							<>
								<Divider />
								<SettingsSwitch
									key="use-devnet"
									label="Use Devnet"
									checked={settings.devnetOverride}
									onChange={() => updateSetting('devnetOverride')}
								/>
							</>
						)}
					</div>
					<div className="border-t border-stroke-divider pt-md">
						<Button
							size="md"
							variant="secondary"
							className="w-full"
							onClick={showSettingsModal}
						>
							More Settings
						</Button>
					</div>
				</PopoverWrapper>
			)}
		</>
	);
}
